import type { AppraiserUser, CustomerUser } from '@einwert/valuation-api-client'
import { CircularProgress, GridLegacy as Grid, Stack, styled, Tooltip } from '@mui/material'
import {
    Alert,
    FloatingButton,
    FormTemplate,
    LoadingSkeleton,
    NotificationSettings,
    NotificationSettingsTable,
    TinyButton,
    tokens,
} from 'ComponentLibrary'
import { PADDING_X } from 'ComponentLibrary/src/molecules/UserSettings/constants'
import { UserSettingsModalFormState } from 'ComponentLibrary/src/molecules/UserSettings/types'
import { Typography } from 'ComponentLibrary/src/piazza'
import {
    getPlausibilityNotificationSettings,
    getMyTasksNotificationSettings,
    getOrderNotificationSettings,
    getOrderStatusNotificationSettings,
    getOtherTasksNotificationSettings,
    getValuationAndDraftsNotificationSettings,
} from 'ClientPortal/src/pages/Settings/constants'
import { ArrowUpRightIcon } from '@phosphor-icons/react'
import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { FloatingSaveContainer } from 'ComponentLibrary/src/atoms/FloatingSaveContainer'
import { Save, X } from 'react-feather'
import { SIDEBAR_WIDTH } from 'ComponentLibrary/src/atoms/Sidebar/constants'
import { getAppraiserUIBaseUrl } from 'ClientPortal/src/common/environment'
import { usePendingFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { useTranslation } from 'react-i18next'
import { getNotificationSettingsTableCopy } from 'ClientPortal/src/pages/Settings/i18n'

const PREFIX = 'settings.notificationSettingsContent.notifications'

function buildDefaults(user: CustomerUser): NotificationSettings {
    return {
        new_message_in_thread_priority: user?.notification_settings.new_message_in_thread_priority,
        new_message_in_thread_with_mention_priority: user?.notification_settings.new_message_in_thread_with_mention_priority ?? 'immediate',
        new_message_in_thread_connected_to_my_task_priority:
            user?.notification_settings.new_message_in_thread_connected_to_my_task_priority,
        new_message_in_thread_connected_to_task_priority: user?.notification_settings.new_message_in_thread_connected_to_task_priority,
        new_task_assignee_in_my_task_priority: user?.notification_settings.new_task_assignee_in_my_task_priority,
        order_blocked_priority: user?.notification_settings.order_blocked_priority,
        valuation_finalised_priority: user?.notification_settings.valuation_finalised_priority,
        order_accepted_priority: user?.notification_settings.order_accepted_priority,
        new_order_due_date_priority: user?.notification_settings.new_order_due_date_priority,
        new_inspection_date_priority: user?.notification_settings.new_inspection_date_priority,
        task_created_priority: user?.notification_settings.task_created_priority,
        my_task_status_transitioned_priority: user?.notification_settings.my_task_status_transitioned_priority,
        task_status_transitioned_priority: user?.notification_settings.task_status_transitioned_priority,
        task_due_tomorrow_priority: user?.notification_settings.task_due_tomorrow_priority,
        task_due_soon_priority: user?.notification_settings.task_due_soon_priority,
        order_document_attached_priority: user?.notification_settings.order_document_attached_priority,
        order_status_transitioned_priority: user?.notification_settings.order_status_transitioned_priority,
        draft_valuation_created_priority: user?.notification_settings.draft_valuation_created_priority,
        value_check_finalised_priority: user?.notification_settings.value_check_finalised_priority,
        plausibility_handed_over_priority: user?.notification_settings.plausibility_handed_over_priority,
        plausibility_new_message_priority: user?.notification_settings.plausibility_new_message_priority,
        plausibility_edited_message_priority: user?.notification_settings.plausibility_edited_message_priority,
        plausibility_deleted_message_priority: user?.notification_settings.plausibility_deleted_message_priority,
        plausibility_internally_approved_priority: user?.notification_settings.plausibility_internally_approved_priority,
        plausibility_approved_priority: user?.notification_settings.plausibility_approved_priority,
    }
}

type NotificationSettingsContentProps = {
    isLoading?: boolean
    user?: AppraiserUser | CustomerUser
    updateSettings: (newSettings: NotificationSettings) => void
    isPatchingUser?: boolean
    layout?: 'page' | 'modal'
    onModalFormStateChange?: (state: UserSettingsModalFormState) => void
}

export const NotificationSettingsContent = ({
    isLoading,
    user,
    updateSettings,
    isPatchingUser,
    layout = 'page',
    onModalFormStateChange,
}: NotificationSettingsContentProps) => {
    const appraiserUIBaseUrl = getAppraiserUIBaseUrl()
    const { isPlausibilityInternalApprovalEnabled } = usePendingFlags()
    const { t } = useTranslation('pages')
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })

    const {
        control,
        formState: { isDirty },
        handleSubmit,
        reset,
        register,
    } = useForm<NotificationSettings>({
        defaultValues: user?.role === 'customer' ? buildDefaults(user) : {},
    })

    const onSubmit = handleSubmit((formData) => {
        updateSettings(formData)
    })

    useEffect(() => {
        if (user?.role === 'customer') {
            reset(buildDefaults(user))
        }
    }, [user, reset])

    useEffect(() => {
        if (layout === 'modal') {
            onModalFormStateChange?.({ isDirty, isPersisting: isPatchingUser, onCancel: reset, onSave: onSubmit })
        }
    }, [isDirty, isPatchingUser, layout, onModalFormStateChange, onSubmit, reset])

    const platform = navigator.userAgent.toLowerCase()
    const isMac = platform.includes('mac')
    const isWindows = platform.includes('win')
    const saveShortcutTooltipTitle = isMac ? tTerminology('shortcuts.mac') : isWindows ? tTerminology('shortcuts.windows') : undefined

    if (isLoading) {
        return (
            <LoadingContainer direction="column" width="100%" $layout={layout}>
                <LoadingSkeleton
                    height="40px"
                    width="240px"
                    sx={{ marginX: layout === 'modal' ? 0 : PADDING_X, marginTop: tokens.SizingSpacing400, transform: 'none' }}
                />
                <LoadingSkeleton
                    height="70px"
                    sx={{ marginX: layout === 'modal' ? 0 : PADDING_X, marginTop: tokens.SizingSpacing600, transform: 'none' }}
                />
                <LoadingSkeleton
                    height="350px"
                    sx={{ marginX: layout === 'modal' ? 0 : PADDING_X, marginTop: tokens.SizingSpacing100, transform: 'none' }}
                />
            </LoadingContainer>
        )
    }

    if (user?.role !== 'customer') {
        return (
            <Alert size="small" severity="info" sx={{ height: 'fit-content', margin: tokens.SizingSpacing400, maxWidth: '780px' }}>
                <Stack direction="column" gap={tokens.SizingSpacing100}>
                    {t(`${PREFIX}.appraiserOnly.message`)}
                    <TinyButton
                        rightIcon={<ArrowUpRightIcon />}
                        onClick={() => {
                            window.open(`${appraiserUIBaseUrl}/orders?settings=notifications`, '_blank')
                        }}
                        label={t(`${PREFIX}.appraiserOnly.action`)}
                    />
                </Stack>
            </Alert>
        )
    }

    const content = user?.role === 'customer' && (
        <NotificationSettingsWrapper direction="column" width="100%" $layout={layout}>
            <Stack direction="row" gap={tokens.SizingSpacing200}>
                <Typography variant="headline2" color={tokens.ColorTextBoldest}>
                    {t(`${PREFIX}.eMailNotifications`)}
                </Typography>
                {isPatchingUser && (
                    <CircularProgress size={tokens.SizingIconM} sx={{ position: 'relative', left: 'unset', top: 'unset' }} />
                )}
            </Stack>
            <Stack direction="column" paddingY={tokens.SizingSpacing300} justifyContent={'space-between'}>
                <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                    {t(`${PREFIX}.subtitle`)}
                </Typography>
                <Stack maxWidth={layout === 'modal' ? '100%' : '800px'}>
                    <Typography variant="textS" color={tokens.ColorTextBold}>
                        <ul>
                            <li>
                                {t(`${PREFIX}.explanation.item1.start`)}
                                <strong>{t(`${PREFIX}.explanation.item1.emphasis`)}</strong>
                                {t(`${PREFIX}.explanation.item1.end`)}
                            </li>
                            <li>
                                {t(`${PREFIX}.explanation.item2.start`)}
                                <strong>{t(`${PREFIX}.explanation.item2.taskEmphasis`)}</strong>
                                {t(`${PREFIX}.explanation.item2.middle`)}
                                <strong>{t(`${PREFIX}.explanation.item2.threadEmphasis`)}</strong>
                                {t(`${PREFIX}.explanation.item2.end`)}
                            </li>
                            <li>
                                {t(`${PREFIX}.explanation.item3.start`)}
                                <strong>{t(`${PREFIX}.explanation.item3.emphasis`)}</strong>
                                {t(`${PREFIX}.explanation.item3.end`)}
                            </li>
                        </ul>
                    </Typography>
                </Stack>
            </Stack>
            <NotificationSettingsTable
                settings={user.notification_settings}
                updateSettings={updateSettings}
                control={control}
                rowGroups={[
                    getOrderNotificationSettings(t, tTerminology),
                    getOrderStatusNotificationSettings(t),
                    getValuationAndDraftsNotificationSettings(t),
                    getMyTasksNotificationSettings(t),
                    getOtherTasksNotificationSettings(t),
                    getPlausibilityNotificationSettings(!!isPlausibilityInternalApprovalEnabled, t, tTerminology),
                ]}
                register={register}
                copy={getNotificationSettingsTableCopy(t)}
                density={layout === 'modal' ? 'compact' : 'regular'}
            />
        </NotificationSettingsWrapper>
    )

    if (layout === 'modal') {
        return content
    }

    return (
        <FormTemplate isPersisting={isPatchingUser} isFormDirty={isDirty} onSubmit={onSubmit} onCancel={reset} hideFooter>
            <Grid item xs={12}>
                {content}
                <FloatingSaveContainer sidebarWidth={SIDEBAR_WIDTH} data-testid="floating-global-save-container">
                    {(isDirty || isPatchingUser) && (
                        <>
                            <FloatingButton
                                onClick={() => reset()}
                                startIcon={<X />}
                                label={tTerminology('actions.discard')}
                                variant={'secondary'}
                                aria-label="Cancel"
                            />
                            <Tooltip title={saveShortcutTooltipTitle} enterDelay={1500} enterNextDelay={1500}>
                                <FloatingButton
                                    onClick={onSubmit}
                                    endIcon={<Save />}
                                    label={tTerminology('actions.save')}
                                    variant={'primary'}
                                    aria-label="Submit"
                                    disabled={isPatchingUser}
                                />
                            </Tooltip>
                        </>
                    )}
                </FloatingSaveContainer>
            </Grid>
        </FormTemplate>
    )
}

const LoadingContainer = styled(Stack, { shouldForwardProp: (prop: string) => prop[0] !== '$' })<{ $layout: 'page' | 'modal' }>(
    ({ $layout }) => ({
        padding: $layout === 'modal' ? tokens.SizingSpacing300 : undefined,
        boxSizing: 'border-box',
    })
)

const NotificationSettingsWrapper = styled(Stack, { shouldForwardProp: (prop: string) => prop[0] !== '$' })<{
    $layout: 'page' | 'modal'
}>(({ $layout }) => ({
    marginTop: $layout === 'modal' ? 0 : tokens.SizingSpacing600,
    marginLeft: $layout === 'modal' ? 0 : '15%',
    marginRight: $layout === 'modal' ? 0 : '15%',
    maxWidth: $layout === 'modal' ? '100%' : 'min(1448px, 70%)',
    padding: $layout === 'modal' ? tokens.SizingSpacing300 : undefined,
    boxSizing: 'border-box',
}))
